/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import isNil from 'lodash.isnil';
import {getDriverScores} from '@ion/applications/src/pages/admin/driverscoring/DriverScoreUtil';
import {speedThreshold} from '@ion/core/src/entities/Asset';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {
  Account,
  CVSAssetType,
  DateFilterOptions,
  GenericObject,
  HVSRecord,
  Report,
  ReportStatus,
  UnitOfMeasure,
  ValidVisionRequestProperties
} from '@ion/core/src/types/entities';
import {metersToKilometers, metersToMiles} from '@ion/core/src/units/conversions';
import {calculateDistance} from '@ion/core/src/utils/DistanceUtils';
import {IonGridAPI} from '@ion/dls/src/grid/IonGrid';
import {calculateTimeDiff} from '@ion/dls/src/utils/TableUtils';
import {TrackingHistoryMapInterface} from '@/types/reports';
import {ReportsMode, ReportTabMode} from '../../types/redux';

// defining the arguments for getParams utility...
interface ParamsArg {
  account?: Account;
  dateFilterOptions?: DateFilterOptions;
}

// defining report params...
enum ReportParams {
  speedingOneToFour = 'Speed0',
  speedingFiveToNine = 'Speed1',
  speedingTenToFourteen = 'Speed2',
  speedingFifteenPlus = 'Speed3',
  nightTimeDriving = 'Night_Weight',
  harshAcceleration = 'Harsh_Acceleration_Weight',
  harshDeceleration = 'Harsh_Braking_Weight',
  hardCornering = 'Hard_Cornering_Weight',
  visionCollisionWarning = 'Vision_Collision_Warning',
  visionDistractedDriving = 'Vision_Distracted_Driving',
  visionTrafficViolation = 'Vision_Traffic_Violation',
  visionLaneDrift = 'Vision_Lane_Drift',
  visionLaneDeparture = 'Vision_Lane_Departure',
  paramInterval = 'param_Interval',
  startDate = 'Start_Date',
  endDate = 'end_date'
};

export enum PeriodicEventAction {
  Show = 'Show',
  Hide = 'Hide'
};

export enum LoadingState {
  Retrieving = 'retrieving',
  Retrieved = 'retrieved',
  Processed = 'processed',
  Done = 'done'
};

export const LoadingStateLabel = {
  [LoadingState.Retrieving] : 'reports:loadingState.retrieving',
  [LoadingState.Retrieved]  : 'reports:loadingState.retrieved',
  [LoadingState.Processed]  : 'reports:loadingState.processed',
  [LoadingState.Done]       : 'reports:loadingState.done'
};

// defines the getScoresParams method's return type...
type ScoreParams = {
  [key in ReportParams]: number;
};

export interface ResolveReportTabParams {
  report?: Report;
  reportURL?: string;
  mode: ReportTabMode;
};

/**
 * @isAssetHistoryMode
 *
 * Returns a boolean value of whether or not the mode
 * passed is equal to the AssetHistory enum value
 */
export const isAssetHistoryMode = (mode?: ReportsMode): boolean => {
  return mode === ReportsMode.AssetHistory;
};

/**
 * @getHarshEvents
 *
 * compares a list of events against our events list and filters
 * the passed list down to ones that contain harsh events
 */
export const getHarshEvents = (events: HVSRecord[] = []): HVSRecord[] => {
  const harshEvents = events.filter((event: HVSRecord): boolean => {
    return event.isSevereEvent;
  });
  return harshEvents;
};

/**
 * @getEventWithEngineAndIdleHours
 * @param events An array of event objects.
 * @returns the first event from events that has both engineHours and idleHours properties.
 * @description
 * This function returns the first event from events that has values for both engineHours and idleHours properties.
 */
const getEventWithEngineAndIdleHours = (events: HVSRecord[]) => {
  return events.find(event => event.engineHours && !isNil(event.idleHours));
};

/**
 * @getIdlingTime
 * @param eventsByAsset: An object containing arrays of events, with the keys being the asset ID.
 * @returns a number representing the accumulated idle time in hours of the assets.
 * @description
 * This function calculates the idle time of assets by iterating through the arrays of events
 * of each asset in eventsByAsset.
 *
 * This function calculates the idle time of assets by iterating through the arrays of events
 *  of each asset in eventsByAsset.
 *
 * For each asset, the first and last events with engine and idle hours information are
 * extracted by calling getEventWithEngineAndIdleHours on the events and the reversed events.
 * The idle time between these events is calculated in minutes by calling getIdleTimeInMinutes
 * with firstEvent and lastEvent as arguments. This idle time is then added to the idleTimeAccumulator
 * and converted to hours.
 *
 * If the asset is camera only, the idle time is calculated by calling calculateCameraOnlyIdleTime
 * with the events and speedThreshold as arguments.
 *
 *Finally, the accumulated idle time in hours is returned as the output of the function.
 */
export const getIdlingTime = (eventsByAsset: GenericObject): number => {
  let idleTimeAccumulator = 0;
  for (const [asset, events] of Object.entries(eventsByAsset)) {
    // Check if Asset is camera only (deviceCategory === 'CAMERA')
    if (isCameraOnly(events)) {
      idleTimeAccumulator += calculateCameraOnlyIdleTime(events, speedThreshold);
    } else {
      const firstEvent = getEventWithEngineAndIdleHours(events);
      const lastEvent = getEventWithEngineAndIdleHours(events.reverse());
      if (firstEvent && lastEvent) {
        idleTimeAccumulator += (getIdleTimeInMinutes(firstEvent, lastEvent) / 60);
      }
    }
  }
  return idleTimeAccumulator;
};

const getIdleTimeInMinutes = (firstEvent, lastEvent) => {
  return Math.abs((firstEvent.idleHours - lastEvent.idleHours));
};

/**
 * @getDrivingTimeByEngineHoursInMinutes
 * @param firstEvent: An event object with engineHours and idleHours properties.
 * @param lastEvent: An event object with engineHours and idleHours properties.
 * @returns  a number representing the driving time in minutes between firstEvent
 * and lastEvent as calculated from their engineHours and idleHours properties.
 * @description
 * This function calculates the driving time between two events, firstEvent and
 * lastEvent, by taking the absolute difference between their engineHours properties
 * and subtracting the idle time between these events as calculated by
 * getIdleTimeInMinutes. The result is returned as the output in minutes.
 */
const getDrivingTimeByEngineHoursInMinutes = (firstEvent, lastEvent) => {
  const engineHours = Math.abs((firstEvent.engineHours - lastEvent.engineHours));
  const idleHours = getIdleTimeInMinutes(firstEvent, lastEvent);
  return engineHours - idleHours;
};

/**
 * @getDrivingTime
 * @param eventsByAsset: An object containing arrays of events, with the keys being the asset name.
 * @returns a number representing the accumulated driving time in hours of the assets.
 * @description
 * This function calculates the driving time of assets by iterating through the
 * arrays of events of each asset in eventsByAsset.
 *
 * For each asset, the first and last events that have engine and idle hours
 * information (determined by calling getEventWithEngineAndIdleHours),
 * the driving time is calculated by calling getDrivingTimeByEngineHoursInMinutes
 * with firstEvent and lastEvent as arguments. This driving time
 * is then added to the driveTimeAccumulator.
 *
 * If the asset is camera only, the driving time is calculated by calling
 * calculateCameraOnlyDrivingTime with the events and speedThreshold as arguments.
 *
 * Finally, the accumulated driving time in hours is returned as the output of the function. Note that
 * the result is returned as the absolute value of driveTimeAccumulator with Math.abs function.
 */
export const getDrivingTime = (eventsByAsset: GenericObject): number => {
  let driveTimeAccumulator = 0;
  for (const [asset, events] of Object.entries(eventsByAsset)) {
    // Check if Asset is camera only (deviceCategory === 'CAMERA')
    if (isCameraOnly(events)) {
      driveTimeAccumulator += calculateCameraOnlyDrivingTime(events, speedThreshold);
    } else {
      const firstEvent = getEventWithEngineAndIdleHours(events);
      const lastEvent = getEventWithEngineAndIdleHours(events.reverse());
      if (firstEvent && lastEvent) {
        driveTimeAccumulator += getDrivingTimeByEngineHoursInMinutes(firstEvent, lastEvent);
      }
    }
  }
  return Math.abs(driveTimeAccumulator);
};

/**
 * @getDistanceTraveled
 *
 * Calculates the total distance traveled of an asset based on the
 * list by taking the most recent event odometer and subtracting
 * the first event odometer reading.
 *
 * In the event that an Asset is camera only, the distance traveled is calculated by summing the deltaDistance.
 */
export const getDistanceTraveled = (
  historyByAsset?: TrackingHistoryMapInterface,
  unitOfMeasure: UnitOfMeasure = UnitOfMeasure.US
): string => {
  let totalDistance = null;
  let isAsterisk = false;
  if (historyByAsset) {
    for (const [asset, events] of Object.entries(historyByAsset)) {
      if (events.length >= 2) {
        if (isCameraOnly(events)) {
          const cameraDistance = calculateCameraOnlyAssetDistance(events, unitOfMeasure);
          totalDistance += cameraDistance;
        } else {
          const convertedEvents = events.map(e => e?.odometer ?? e?.gpsOdometer);
          const assetDistance = calculateDistance(convertedEvents);
          if (isNil(assetDistance)) {
            isAsterisk = true;
          } else {
            totalDistance += assetDistance;
          }
        }
      }
    }
  }
  return !isNil(totalDistance) ? totalDistance.toFixed(1) + (isAsterisk ? '*' : '') : null;
};

/**
 * @makeDurationList
 *
 * Helper function to make a list in memory that tracks
 * an event duration for an item by comparing it to its
 * predecessor
 */
export const makeDurationList = (list?: CVSAssetType[]): {
  duration: {
    hours: number;
    minutes: number;
  },
  id: number;
}[] => {
  const durationList = [];
  list?.forEach((item, i) => {
    const diff = calculateTimeDiff(
      list[i + 1], item || null
    );
    durationList.push({
      id       : item.OBJECTID,
      duration : diff
    });
  });
  return durationList;
};

/**
 * @getFleetOutlookPreferences
 *
 * Returns the fleetOutlook preference if the user has, else returns false.
 */
export function getFleetOutlookPreferences() {
  const store = UserPreferences.getStore();
  return store?.fleetOutlook ?? false;
}

/**
 * @hasValidFOSettings
 *
 * Helper function to validate given is FleetOutlook parameters.
 */
export const hasValidFOSettings = (fleetOutlook: GenericObject) => {
  if (fleetOutlook) {
    const {environment, enterpriseCode, userId} = fleetOutlook;
    return !!(environment.length && enterpriseCode.length && userId.length);
  }
  return false;
};

const iOnApplicationName = 'AO';

export const getAHRReport = () => ({
  name            : translate('reports:cards.iOnReports.assetHistory.title'),
  description     : translate('reports:cards.iOnReports.assetHistory.description'),
  reportType      : ReportsMode.AssetHistory,
  applicationName : [iOnApplicationName],
  categoryName    : translate('reports:cards.iOnReports.assetHistory.categoryName'),
  reportStatus    : ReportStatus.new
});

export const getShipmentHistoryReport = () => ({
  name            : translate('reports:cards.iOnReports.shipmentHistory.title'),
  description     : translate('reports:cards.iOnReports.shipmentHistory.description'),
  reportType      : ReportsMode.ShipmentHistory,
  applicationName : [iOnApplicationName],
  categoryName    : translate('reports:cards.iOnReports.shipmentHistory.categoryName'),
  reportStatus    : ReportStatus.new
});

/**
 * The utility takes an account as a input and returns the key & value pair of params that
 * required to pass to the Driver scorecard V2 reports.
 *
 * ```typescript
 *
 *  import {getScoresParams} from './reportUtils';
 *
 *  const scoreParams = getScoresParams({account});
 *
 * ```
 */
export const getScoresParams = ({account}: ParamsArg): ScoreParams => {
  const scores = getDriverScores(account);
  const params = {} as ScoreParams;
  Object.entries(scores)?.forEach(([key, value]) => {
    params[ReportParams[key]] = value;
  });
  return params;
};

/**
 * The utility function takes a date history and event trip flag as a input and return the total
 * number of assets and events that are actually showing in grid/map. The return array contains
 * total asset count in index 0 and total event count in index 1.
 *
 * ```typescript
 *
 *  import {getTotalAssetAndEvents} from './reportUtils';
 *
 *  const {totalAssets, totalEvents} = getTotalAssetAndEvents(history, true);
 *
 * ```
 */
export const getTotalAssetAndEvents = (history, showEventsBetweenTrips = false) => {
  const assetMap  = {};
  let assetsCount = 0;
  let eventsCount = 0;
  const isCountEvent = (tripGroup: number | undefined) => tripGroup || showEventsBetweenTrips;

  history?.forEach(({assetId, tripGroup, messageUuid, gridVisibility}) => {
    if (gridVisibility) {
      const asset = assetMap[assetId];
      if (!asset) {
        assetsCount++;
        isCountEvent(tripGroup) && eventsCount++;
        assetMap[assetId] = [messageUuid];
      } else if (asset && !asset.includes(messageUuid)) {
        asset.push(messageUuid);
        isCountEvent(tripGroup) && eventsCount++;
      }
    }
  });
  return [assetsCount, eventsCount];
};

// helper method helps to determines  mode...
const isMode = (reportMode: ReportTabMode) => (mode: ReportTabMode) => mode === reportMode;

// determines whether the passing mode is view or not...
export const isDefaultMode = isMode(ReportTabMode.Default);

// determines whether the passing mode is asset history mode or not...
export const isAHRMode = isMode(ReportTabMode.AssetHistory);

// determines whether the passing mode is shipment history mode or not...
export const isSHRMode = isMode(ReportTabMode.ShipmentHistory);

/**
 * Handle hover or click events that come from the map and highlight the respective row.
 * The setSelected method provides us with a second argument that, if true, clears any
 * other selection states so we want to do that on hover, but persist a selection state
 * for a row that is clicked.
 */
export const handleMapHoverOrClick = (gridApi: IonGridAPI, hoveredEvent: HVSRecord) => {
  const {isMapClick, OBJECTID} = hoveredEvent;
  let row                      = gridApi.getRowNode(OBJECTID);
  if (row) {
    const {parent} = row;
    // highlight the row in grid
    row.setSelected(true, !isMapClick);
    if (isMapClick) {
      const {expanded : isRowGroupExpanded} = parent || {};
      // expand the highlighted row's group if it's collapsed
      let expandDelay = 0;
      if (!isRowGroupExpanded) {
        parent?.setExpanded?.(true);
        expandDelay = 300;
      }
      const timeout = setTimeout(() => {
        // update the row by the latest node
        row = gridApi.getRowNode(row.data?.OBJECTID);
        // make sure the node is visible in the grid(auto scroll)
        gridApi.ensureNodeVisible(row, 'middle');
        clearTimeout(timeout);
      }, expandDelay);
    }
  }
};

/**
 * The utility function takes a device architecture, device category and video url as a input and return
 * the boolean value that determines whether the vision icon should be shown or not.
 *
 */
export const validateVisionRequestProperties = (deviceArchitecture: string, deviceCategory: string) => {
  const validCategory     = ValidVisionRequestProperties.Category;
  const validArchitecture = ValidVisionRequestProperties.Architecture;
  return deviceArchitecture === validArchitecture && deviceCategory === validCategory;
};

/**
 * @getTimeDuration
 * This utility function takes duration and durationType as input and returns the duration in minutes or hours.
 *
 * ```typescript
 *
 * import {getTimeDuration} from './reportUtils';
 *
 * const duration = getTimeDuration(60, 'minutes');
 *
 * ```
 *
 * @param duration - duration in minutes
 * @param durationType - minutes or hours
 * @returns duration in minutes or hours
 */
export const getTimeDuration = (duration: number, durationType: 'minutes' | 'hours' = 'minutes') => {
  if (isNil(duration)) {
    return null;
  }
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  return durationType === 'minutes' ? minutes : hours;
};

/**
 * Calculates the total idle time from an array of vehicle sensor records.
 * @param {Object[]} hvsRecords - Array of events.
 * @param {number} movingThreshold - The speed below which the vehicle is considered idle.
 * @returns {number} The total idle time rounded to the nearest minute.
 */
export const calculateCameraOnlyIdleTime = (hvsRecords: Array<HVSRecord>, movingThreshold: number = 1) => {
  let totalIdleTime = 0;
  // Sort the records by event time.
  hvsRecords.sort((a, b) => a.eventTime - b.eventTime);
  // Calculate idle time incrementally between each pair of records.
  for (let i = 0; i < hvsRecords.length - 1; i++) {
    const currentRecord = hvsRecords[i];
    const nextRecord = hvsRecords[i + 1];

    // Compute the time delta between the current and the next record.
    const deltaDuration = (nextRecord.eventTime - currentRecord.eventTime) / 1000 / 60; // Convert to minutes

    // If the ignition is ON and gpsSpeed is less than the moving threshold, add to idle time.
    if (currentRecord.ignition === 'true' && currentRecord.gpsSpeed < movingThreshold) {
      totalIdleTime += deltaDuration;
    }
  }

  return Math.round(totalIdleTime);
};

/**
 * @calculateCameraOnlyDrivingTime
 * Calculates the total driving time from an array of vehicle sensor records.
 *
 * @param {Object[]} hvsRecords - Array of events.
 * @param {number} movingThreshold - The speed above which the vehicle is considered moving.
 * @returns {number} The total driving time rounded to the nearest minute.
 */
export const calculateCameraOnlyDrivingTime = (hvsRecords, movingThreshold: number = 1) => {
  let totalDrivingTime = 0;
  hvsRecords.sort((a, b) => a.eventTime - b.eventTime);
  // calculate the deltaDuration if the ignition is 'true' and the speed is above the moving threshold.
  for (let i = 0; i < hvsRecords.length; i++) {
    const currentRecord = hvsRecords[i];
    // As long as there is a next record, calculate the deltaDuration
    let deltaDuration = 0;
    if (i < hvsRecords.length - 1) {
      const nextRecord = hvsRecords[i + 1];
      deltaDuration = (nextRecord.eventTime - currentRecord.eventTime) / 1000; // Convert to minutes
    }
    // Check if the vehicle is driving.
    if (currentRecord.ignition === 'true' && currentRecord.gpsSpeed >= movingThreshold) {
      totalDrivingTime += deltaDuration;
    }
  }

  return Math.round(totalDrivingTime);
};

/**
 * @isCameraOnly
 * Determines if an Asset is camera only by checking the first event in the array.
 *
 * @param events - Array of events.
 * @returns
 */
export const isCameraOnly = (events: HVSRecord[]): boolean => {
  return events?.length > 0 && events[0]?.isCameraOnly;
};

/**
 * @calculateCameraOnlyAssetDistance
 * Calculates the total distance traveled by for an Camera Only Asset by summing the deltaDistance.
 *
 * @param events - Array of events.
 * @param unitOfMeasure - The user's preferred unit of measure.
 * @returns
 */
export const calculateCameraOnlyAssetDistance = (events: HVSRecord[], unitOfMeasure: UnitOfMeasure): number => {
  const distance = events.reduce((acc, event) => {
    const {deltaDistance} = event || {};
    return !isNil(deltaDistance) ? acc + deltaDistance : acc;
  }, 0);
  // The deltaDistance is in meters, so we need to convert to
  // miles or kilometers based on the user's preferred unit of measure
  return unitOfMeasure === UnitOfMeasure.Metric ? metersToKilometers(distance) : metersToMiles(distance);
};

/**
 * Retrieves trip-related metadata from the first leaf child when grouping is applied,
 * otherwise falls back to the current row data. This ensures grouped rows inherit
 * the trip service state and trip type from their underlying records.
 */
export const getTripServiceAndTripType = (allLeafChildren: any, data: any) => {
  const {isTripServicesEnabled, tripType} = allLeafChildren?.[0]?.data ?? data ?? {};

  return {isTripServicesEnabled, tripType};
};

/**
 * Returns the starting time from a table date range string.
 * 
 * The input is expected to be a date range in the format:
 * 
 * ```text
 * MM/DD/YYYY, HH:MM AM/PM - MM/DD/YYYY, HH:MM AM/PM
 * ```
 * 
 * For example:
 * 
 * ```text
 * 02/25/2026, 12:00 AM - 02/25/2026, 11:59 PM
 * ```
 * 
 * will return:
 * 
 * ```text
 * 12:00 AM
 * ```
 */
export const getHistoryStartTime = (tableDates: string) => {
  const startDate = tableDates?.split(' - ')?.[0];

  return new Date(startDate).toLocaleTimeString([], {
    hour   : 'numeric',
    minute : '2-digit'
  });
};
